import {SetStateAction, useLayoutEffect, useRef, useSyncExternalStore} from "react";

class ComputedStateStore<T> {
  private callbacks: Set<() => void> = new Set();

  constructor(private snapshot: T) {}

  subscribe = (callback: () => void) => {
    this.callbacks.add(callback);
    return () => {
      this.callbacks.delete(callback);
    };
  };

  getSnapshot = (): T => {
    return this.snapshot;
  };

  updateWithoutRerender(newSnapshot: T) {
    this.snapshot = newSnapshot;
  }

  update = (action: SetStateAction<T>): T => {
    const newSnapshot = typeof action === "function" ? (action as (prevState: T) => T)(this.snapshot) : action;
    if (this.snapshot !== newSnapshot) {
      this.snapshot = newSnapshot;
      for (const cb of this.callbacks) {
        cb();
      }
    }
    return this.snapshot;
  };
}

function useComputedState<T>(computeState: (prevState: T | undefined) => T) {
  const ref = useRef<ComputedStateStore<T> | null>(null);
  if (ref.current === null) {
    ref.current = new ComputedStateStore<T>(computeState(undefined));
  }
  const state = useSyncExternalStore(ref.current.subscribe, ref.current.getSnapshot, ref.current.getSnapshot);
  const newState = computeState(state);

  useLayoutEffect(() => {
    if (newState !== state) {
      ref.current!.updateWithoutRerender(newState);
    }
  }, [newState, state]);

  return [newState, ref.current.update] as const;
}

export default useComputedState;
